import { AlertTriangle, CheckCircle2, Cpu, DownloadCloud, Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useLocalLLM } from '@/contexts/LocalLLMContext';

interface LocalModelStatusCardProps {
  modelName?: string | null;
  compact?: boolean;
}

const statusConfig = {
  downloading: {
    label: 'Downloading model',
    icon: DownloadCloud,
    badgeClassName: 'bg-sky-600 text-white hover:bg-sky-600',
  },
  initializing: {
    label: 'Initializing runtime',
    icon: Loader2,
    badgeClassName: 'bg-violet-600 text-white hover:bg-violet-600',
  },
  generating: {
    label: 'Generating on device',
    icon: Sparkles,
    badgeClassName: 'bg-indigo-600 text-white hover:bg-indigo-600',
  },
  ready: {
    label: 'Running locally',
    icon: CheckCircle2,
    badgeClassName: 'bg-emerald-600 text-white hover:bg-emerald-600',
  },
  error: {
    label: 'Local model error',
    icon: AlertTriangle,
    badgeClassName: 'bg-destructive text-destructive-foreground hover:bg-destructive',
  },
  webgpu_not_supported: {
    label: 'WebGPU unavailable',
    icon: AlertTriangle,
    badgeClassName: 'bg-amber-600 text-white hover:bg-amber-600',
  },
  idle: {
    label: 'Waiting',
    icon: Cpu,
    badgeClassName: 'bg-muted text-foreground hover:bg-muted',
  },
} as const;

export default function LocalModelStatusCard({ modelName, compact = false }: LocalModelStatusCardProps) {
  const { status } = useLocalLLM();
  const state = status.state || 'idle';
  const config = statusConfig[state as keyof typeof statusConfig] || statusConfig.idle;
  const Icon = config.icon;
  const progressValue = typeof status.progress?.loaded === 'number'
    ? Math.max(0, Math.min(100, status.progress.loaded))
    : 0;
  const stageText = status.progress?.stage || (state === 'downloading'
    ? 'Downloading model files to local app/browser cache…'
    : state === 'initializing'
      ? 'Preparing the on-device runtime and compiling GPU shaders…'
      : state === 'generating'
        ? 'Generating a response on your device…'
        : state === 'ready'
          ? 'Ready to respond on your device.'
          : state === 'webgpu_not_supported'
            ? 'This browser or desktop surface does not expose WebGPU.'
            : status.error || 'Preparing local inference…');

  return (
    <div className={`rounded-xl border bg-card/90 ${compact ? 'p-3' : 'p-4 shadow-sm'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border bg-muted/40">
            <Icon className={`h-4 w-4 ${state === 'initializing' || state === 'generating' ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <div className="text-sm font-semibold">{modelName || 'Local browser model'}</div>
            <div className="text-xs text-muted-foreground">Runs on your device with MapleOS local WebLLM</div>
          </div>
        </div>
        <Badge className={config.badgeClassName}>{config.label}</Badge>
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex items-start justify-between gap-3 text-sm">
          <p className="text-muted-foreground">{stageText}</p>
          {state === 'downloading' || state === 'initializing' ? (
            <span className="shrink-0 text-xs font-medium text-muted-foreground">{progressValue}%</span>
          ) : null}
        </div>

        {state === 'downloading' || state === 'initializing' ? (
          <Progress value={progressValue} className="h-2" />
        ) : null}

        {status.webGpuInfo?.shaderF16Supported === false ? (
          <p className="text-xs text-muted-foreground">This WebGPU runtime does not expose shader-f16 support.</p>
        ) : null}

        {status.warning ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">{status.warning}</p>
        ) : null}

        {status.error ? (
          <p className="text-xs text-destructive">{status.error}</p>
        ) : null}
      </div>
    </div>
  );
}